import { geoEqualEarth } from "d3-geo";

const CAMERA_SCALE_MIN = 200;
const CAMERA_SCALE_MAX = 800;
const CENTER_LNG_MIN = -180;
const CENTER_LNG_MAX = 180;
const CENTER_LAT_MIN = -60;
const CENTER_LAT_MAX = 80;

function getDefaultCamera(mobile) {
  return mobile
    ? { scale: 320, center: [0, 15] }
    : { scale: 228, center: [0, 12] };
}

function clampCamera(cam) {
  const s = Number.isFinite(cam?.scale) ? cam.scale : CAMERA_SCALE_MIN;
  const c0 = Number.isFinite(cam?.center?.[0]) ? cam.center[0] : 0;
  const c1 = Number.isFinite(cam?.center?.[1]) ? cam.center[1] : 12;
  return {
    scale: Math.min(CAMERA_SCALE_MAX, Math.max(CAMERA_SCALE_MIN, s)),
    center: [
      Math.min(CENTER_LNG_MAX, Math.max(CENTER_LNG_MIN, c0)),
      Math.min(CENTER_LAT_MAX, Math.max(CENTER_LAT_MIN, c1)),
    ],
  };
}

function zoomCameraAroundSvgPoint(prevCam, svgXY, nextScaleRaw, width, height) {
  if (
    !prevCam ||
    !Array.isArray(prevCam.center) ||
    prevCam.center.length < 2 ||
    !Array.isArray(svgXY) ||
    svgXY.length < 2 ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return clampCamera({
      scale: Number.isFinite(nextScaleRaw) ? nextScaleRaw : CAMERA_SCALE_MIN,
      center: prevCam?.center ?? [0, 12],
    });
  }
  const nextScale = Math.min(CAMERA_SCALE_MAX, Math.max(CAMERA_SCALE_MIN, nextScaleRaw));
  const proj0 = geoEqualEarth()
    .scale(prevCam.scale)
    .center(prevCam.center)
    .translate([width / 2, height / 2]);
  const lnglat = proj0.invert(svgXY);
  if (!lnglat || !Number.isFinite(lnglat[0]) || !Number.isFinite(lnglat[1])) {
    return clampCamera({ scale: nextScale, center: prevCam.center });
  }
  let c = [prevCam.center[0], prevCam.center[1]];
  for (let i = 0; i < 18; i += 1) {
    const p = geoEqualEarth().scale(nextScale).center(c).translate([width / 2, height / 2]);
    const xy = p(lnglat);
    if (!xy || !Number.isFinite(xy[0]) || !Number.isFinite(xy[1])) break;
    const ex = svgXY[0] - xy[0];
    const ey = svgXY[1] - xy[1];
    if (!Number.isFinite(ex) || !Number.isFinite(ey)) break;
    if (ex * ex + ey * ey < 0.09) break;
    const k = 0.48 / nextScale;
    c[0] += ex * k;
    c[1] -= ey * k;
    c[0] = Math.min(CENTER_LNG_MAX, Math.max(CENTER_LNG_MIN, c[0]));
    c[1] = Math.min(CENTER_LAT_MAX, Math.max(CENTER_LAT_MIN, c[1]));
  }
  return clampCamera({ scale: nextScale, center: c });
}

function clientPointToSvgUser(svgEl, clientX, clientY) {
  if (typeof SVGSVGElement === "undefined" || !svgEl) return null;
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return null;
  let inv = null;
  try {
    inv = ctm.inverse();
  } catch {
    return null;
  }
  const p = pt.matrixTransform(inv);
  if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
  return [p.x, p.y];
}

export {
  CAMERA_SCALE_MIN,
  CAMERA_SCALE_MAX,
  getDefaultCamera,
  clampCamera,
  zoomCameraAroundSvgPoint,
  clientPointToSvgUser,
};
